// ===========================================
// Message Thread Component
// ===========================================
// Displays messages in a conversation with auto-scroll

import { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Alert,
  Divider,
} from '@mui/material';
import { format, isToday, isYesterday } from 'date-fns';
import { Message } from '../../api/messages.api';
import MessageBubble from './MessageBubble';

// ===========================================
// TYPES
// ===========================================

interface MessageThreadProps {
  messages: Message[];
  currentUserId: string;
  loading?: boolean;
  otherParticipantName?: string;
}

// ===========================================
// HELPER FUNCTIONS
// ===========================================

function formatDateDivider(date: Date): string {
  if (isToday(date)) {
    return 'Today';
  }
  if (isYesterday(date)) {
    return 'Yesterday';
  }
  return format(date, 'EEEE, d MMMM yyyy');
}

function groupMessagesByDate(messages: Message[]): Map<string, Message[]> {
  const grouped = new Map<string, Message[]>();

  messages.forEach((message) => {
    const date = new Date(message.createdAt);
    const dateKey = format(date, 'yyyy-MM-dd');

    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }
    grouped.get(dateKey)!.push(message);
  });

  return grouped;
}

// ===========================================
// COMPONENT
// ===========================================

export default function MessageThread({
  messages,
  currentUserId,
  loading = false,
  otherParticipantName,
}: MessageThreadProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              justifyContent: i % 2 === 0 ? 'flex-start' : 'flex-end',
              mb: 2,
            }}
          >
            <Skeleton
              variant="rounded"
              width={200 + Math.random() * 100}
              height={60}
            />
          </Box>
        ))}
      </Box>
    );
  }

  if (messages.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 3,
        }}
      >
        <Alert severity="info" sx={{ maxWidth: 400 }}>
          No messages yet. {otherParticipantName
            ? `Send a message to ${otherParticipantName} to start the conversation!`
            : 'Send a message to start the conversation!'}
        </Alert>
      </Box>
    );
  }

  // Group messages by date and reverse for display (oldest first)
  const sortedMessages = [...messages].reverse();
  const groupedMessages = groupMessagesByDate(sortedMessages);

  return (
    <Box
      sx={{
        flex: 1,
        overflow: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {Array.from(groupedMessages.entries()).map(([dateKey, dateMessages]) => {
        const date = new Date(dateKey);

        return (
          <Box key={dateKey}>
            {/* Date divider */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                my: 2,
              }}
            >
              <Divider sx={{ flex: 1 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ px: 2 }}
              >
                {formatDateDivider(date)}
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>

            {/* Messages for this date */}
            {dateMessages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                isOwn={message.senderId === currentUserId}
              />
            ))}
          </Box>
        );
      })}

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />
    </Box>
  );
}
